/**
 * Snapshot payload codec for embedded diagrams.
 *
 * An embed snapshot travels in the iframe URL fragment (`#data=<payload>`)
 * or in a blog-hosted JSON file, so it must be compact, URL-safe, and
 * self-describing. This module converts between the structured payload and
 * its wire form: versioned JSON → deflate-raw compression → base64url.
 *
 * Compression uses the browser-native CompressionStream/DecompressionStream
 * (also global in Node >= 18) — no extra dependency.
 *
 * Usage:
 *   const encoded = await encodeEmbedPayload({ v: 1, iife, title });
 *   const payload = await decodeEmbedPayload(encoded); // throws EmbedPayloadError subclasses
 */

/** Schema version 1 of the embed snapshot payload. */
export interface EmbedPayload {
  /** Schema version — bump when the payload shape changes. */
  v: 1;
  /** Pre-bundled user code (IIFE), ready for `SimWorkerBridge.init`. */
  iife: string;
  /** Optional display title for the embed. */
  title?: string;
}

/** Base class for all decode failures, so callers can catch one type. */
export class EmbedPayloadError extends Error {}

/** The input is not valid base64url or the compressed bytes are corrupt/truncated. */
export class CorruptPayloadError extends EmbedPayloadError {}

/** The decompressed bytes are not valid JSON. */
export class InvalidPayloadJsonError extends EmbedPayloadError {}

/** The JSON declares a schema version this build does not understand. */
export class UnsupportedPayloadVersionError extends EmbedPayloadError {}

/** The JSON is missing a required field or a field has the wrong type. */
export class InvalidPayloadShapeError extends EmbedPayloadError {}

/**
 * Encode a snapshot payload into a URL-fragment-safe string
 * (base64url: only `A-Z a-z 0-9 - _`, no padding).
 */
export async function encodeEmbedPayload(
  payload: EmbedPayload,
): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(payload));
  const compressed = await transformBytes(
    json,
    new CompressionStream("deflate-raw"),
  );
  return bytesToBase64Url(compressed);
}

/**
 * Decode a string produced by {@link encodeEmbedPayload} back into a
 * validated payload. Never fails silently — throws an
 * {@link EmbedPayloadError} subclass describing what went wrong.
 */
export async function decodeEmbedPayload(
  encoded: string,
): Promise<EmbedPayload> {
  const compressed = base64UrlToBytes(encoded);

  let json: string;
  try {
    const bytes = await transformBytes(
      compressed,
      new DecompressionStream("deflate-raw"),
    );
    json = new TextDecoder().decode(bytes);
  } catch (cause) {
    throw new CorruptPayloadError(
      "Embed payload is corrupt or truncated: decompression failed",
      { cause },
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (cause) {
    throw new InvalidPayloadJsonError(
      "Embed payload decompressed but is not valid JSON",
      { cause },
    );
  }

  return validateEmbedPayload(parsed);
}

/**
 * Validate already-parsed JSON against the version-1 payload schema.
 *
 * Used directly for the `?src=<url>` transport, where the blog hosts the
 * payload as plain (uncompressed) JSON instead of the base64url fragment
 * form. Throws the same {@link EmbedPayloadError} subclasses as
 * {@link decodeEmbedPayload}.
 */
export function validateEmbedPayload(parsed: unknown): EmbedPayload {
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new InvalidPayloadShapeError("Embed payload must be a JSON object");
  }
  const record: Record<string, unknown> = { ...parsed };

  if (record.v !== 1) {
    throw new UnsupportedPayloadVersionError(
      `Unsupported embed payload version: ${JSON.stringify(record.v)}`,
    );
  }
  if (typeof record.iife !== "string") {
    throw new InvalidPayloadShapeError(
      "Embed payload is missing required string field 'iife'",
    );
  }
  if (record.title !== undefined && typeof record.title !== "string") {
    throw new InvalidPayloadShapeError(
      "Embed payload field 'title' must be a string when present",
    );
  }
  return { v: 1, iife: record.iife, title: record.title };
}

/**
 * Run bytes through a TransformStream (compression or decompression) and
 * collect the output. Reading and writing run concurrently so internal
 * stream buffers cannot deadlock on large inputs.
 */
async function transformBytes(
  // Uint8Array<ArrayBuffer> (not ArrayBufferLike): WritableStream<BufferSource>
  // rejects views over SharedArrayBuffer.
  input: Uint8Array<ArrayBuffer>,
  transform: {
    readable: ReadableStream<Uint8Array>;
    writable: WritableStream<BufferSource>;
  },
): Promise<Uint8Array> {
  const writer = transform.writable.getWriter();
  const writeDone = (async () => {
    await writer.write(input);
    await writer.close();
  })();

  const readDone = (async () => {
    const reader = transform.readable.getReader();
    const chunks: Uint8Array[] = [];
    let total = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      total += value.length;
    }
    return { chunks, total };
  })();

  // Settle both sides before throwing: corrupt input errors both ends of the
  // transform, and bailing on the first rejection would leave the other
  // promise as an unhandled rejection.
  const [writeResult, readResult] = await Promise.allSettled([
    writeDone,
    readDone,
  ]);
  if (readResult.status === "rejected") {
    throw readResult.reason;
  }
  if (writeResult.status === "rejected") {
    throw writeResult.reason;
  }

  const { chunks, total } = readResult.value;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  // Build the binary string in chunks: String.fromCharCode(...bytes) would
  // blow the argument limit on large payloads.
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlToBytes(encoded: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/.test(encoded)) {
    throw new CorruptPayloadError(
      "Embed payload is not valid base64url (unexpected characters or empty input)",
    );
  }
  const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
  let binary: string;
  try {
    binary = atob(base64);
  } catch (cause) {
    throw new CorruptPayloadError(
      "Embed payload is corrupt or truncated: base64 decoding failed",
      { cause },
    );
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
